import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DataService } from './data.service';

export interface Language {
	code: string;
	name: string;
	direction: 'ltr' | 'rtl';
}

export interface TranslationItem {
	id: string;
	text: string;
}

@Injectable({
	providedIn: 'root'
})
export class TranslationService {
	private readonly SESSION_STORAGE_KEY = 'selectedLanguage';
	private readonly DEFAULT_LANGUAGE = 'en';

	public readonly SUPPORTED_LANGUAGES: Language[] = [
		{ code: 'en', name: 'English', direction: 'ltr' },
		{ code: 'es', name: 'Spanish', direction: 'ltr' },
		{ code: 'zh-Hans', name: 'Mandarin', direction: 'ltr' },
		{ code: 'ar', name: 'Arabic', direction: 'rtl' },
		{ code: 'de', name: 'German', direction: 'ltr' }
	];

	private currentLanguageSubject = new BehaviorSubject<string>(this.getStoredLanguage());
	public currentLanguage$ = this.currentLanguageSubject.asObservable();

	constructor(private dataService: DataService) {}

	/**
	 * Get the currently selected language from session storage
	 */
	private getStoredLanguage(): string {
		const stored = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
		return stored || this.DEFAULT_LANGUAGE;
	}

	/**
	 * Get the current language code
	 */
	getCurrentLanguage(): string {
		return this.currentLanguageSubject.value;
	}

	/**
	 * Get the current language object with full details
	 */
	getCurrentLanguageDetails(): Language {
		const code = this.getCurrentLanguage();
		return this.SUPPORTED_LANGUAGES.find(l => l.code === code) || this.SUPPORTED_LANGUAGES[0];
	}

	/**
	 * Set the current language and store in session storage
	 */
	setLanguage(languageCode: string): void {
		if (this.SUPPORTED_LANGUAGES.some(l => l.code === languageCode)) {
			sessionStorage.setItem(this.SESSION_STORAGE_KEY, languageCode);
			this.currentLanguageSubject.next(languageCode);
		}
	}

	/**
	 * Translate a page's content
	 * Returns original text if language is English or on error
	 * Server handles caching, so we always call the API
	 */
	translatePage(
		employerId: string,
		pagePath: string,
		items: TranslationItem[]
	): Observable<{ [key: string]: string }> {
		const currentLang = this.getCurrentLanguage();

		// If English, return original text as-is
		if (currentLang === 'en') {
			const translations: { [key: string]: string } = {};
			items.forEach(item => {
				translations[item.id] = item.text;
			});
			return of(translations);
		}

		// Call API to translate - server handles caching
		return this.dataService.translatePage(
			employerId,
			'en',
			currentLang,
			pagePath,
			items
		).pipe(
			map(response => response.translations),
			catchError(error => {
				console.error('Translation error:', error);
				// On error, return original English text
				const translations: { [key: string]: string } = {};
				items.forEach(item => {
					translations[item.id] = item.text;
				});
				return of(translations);
			})
		);
	}

	/**
	 * Check if current language is RTL
	 */
	isRTL(): boolean {
		return this.getCurrentLanguageDetails().direction === 'rtl';
	}
}
